import type {
  Document,
  RAGEnv,
  AutoIndexingConfig,
  RAGPipelineInfo,
  RAGAnalyticsEvent,
} from '../types';
import { AutoDocumentProcessor } from '../processing/auto-document-processor';
import { RAGPipeline } from '../pipeline/rag-pipeline';
import { generateId } from '../core/utils';

/**
 * Continuous indexing system with background monitoring
 * Automatically detects, processes, and indexes new documents
 */
export class ContinuousIndexer {
  private env: RAGEnv;
  private processor: AutoDocumentProcessor;
  private pipelines: Map<string, RAGPipeline> = new Map();
  private isRunning = false;
  private processedFiles: Set<string> = new Set();
  private lastIndexTime = 0;

  constructor(env: RAGEnv, config: AutoIndexingConfig) {
    this.env = env;
    this.processor = new AutoDocumentProcessor(env, config);
  }

  /**
   * Start continuous monitoring and indexing
   */
  async start(): Promise<void> {
    if (this.isRunning) {
      console.warn('Continuous indexer already running');
      return;
    }

    this.isRunning = true;
    console.log('Starting continuous indexing...');

    try {
      // Load existing state
      await this.loadState();

      // Set up monitoring systems
      await this.setupMonitoring();

      // Start initial indexing
      await this.performInitialIndexing();

      // Set up periodic checks
      this.schedulePeriodicIndexing();

      console.log('Continuous indexing started successfully');
    } catch (error) {
      this.isRunning = false;
      throw new Error(`Failed to start continuous indexing: ${error}`);
    }
  }

  /**
   * Stop continuous monitoring
   */
  async stop(): Promise<void> {
    this.isRunning = false;
    console.log('Stopping continuous indexing...');
    
    // Save current state
    await this.saveState();
    
    console.log('Continuous indexing stopped');
  }

  /**
   * Register a RAG pipeline for continuous indexing
   */
  async registerPipeline(pipeline: RAGPipeline, pipelineName: string): Promise<void> {
    this.pipelines.set(pipelineName, pipeline);
    console.log(`Registered pipeline: ${pipelineName}`);
  }

  /**
   * Perform initial indexing of existing documents
   */
  private async performInitialIndexing(): Promise<void> {
    console.log('Performing initial document indexing...');
    
    try {
      const documents = await this.processor.processAutoSources();
      console.log(`Found ${documents.length} documents for initial indexing`);

      if (documents.length === 0) {
        return;
      }

      // Process documents in batches to avoid overwhelming the system
      const batchSize = 10;
      const batches = this.createBatches(documents, batchSize);

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        console.log(`Processing batch ${i + 1}/${batches.length} (${batch.length} documents)`);

        await this.processBatch(batch);

        // Add processed files to tracking set
        batch.forEach(doc => {
          if (doc.metadata.filePath) {
            this.processedFiles.add(doc.metadata.filePath);
          }
        });

        // Small delay between batches to be respectful of resources
        if (i < batches.length - 1) {
          await this.delay(1000);
        }
      }

      this.lastIndexTime = Date.now();
      console.log('Initial indexing completed');
    } catch (error) {
      console.error('Initial indexing failed:', error);
      throw error;
    }
  }

  /**
   * Set up monitoring systems
   */
  private async setupMonitoring(): Promise<void> {
    // Set up R2 event monitoring if available
    await this.setupR2Monitoring();

    // Set up webhook monitoring
    await this.setupWebhookMonitoring();

    // Set up file system monitoring (if applicable)
    await this.setupFileSystemMonitoring();
  }

  /**
   * Set up R2 bucket monitoring
   */
  private async setupR2Monitoring(): Promise<void> {
    // In a real implementation, this would set up R2 event notifications
    // For now, we'll use polling-based detection
    console.log('R2 monitoring setup (polling-based)');
  }

  /**
   * Set up webhook monitoring for external document sources
   */
  private async setupWebhookMonitoring(): Promise<void> {
    // This would be handled by the main Worker's webhook endpoints
    console.log('Webhook monitoring configured');
  }

  /**
   * Set up file system monitoring (for local development)
   */
  private async setupFileSystemMonitoring(): Promise<void> {
    // Not applicable in Cloudflare Workers environment
    console.log('File system monitoring not applicable in Workers environment');
  }

  /**
   * Schedule periodic indexing checks
   */
  private schedulePeriodicIndexing(): void {
    // In Cloudflare Workers, this would be handled by Cron Triggers
    // For now, simulate with setTimeout
    const interval = 5 * 60 * 1000; // 5 minutes

    const scheduleNext = () => {
      if (!this.isRunning) return;

      setTimeout(async () => {
        try {
          await this.performIncrementalIndexing();
          scheduleNext();
        } catch (error) {
          console.error('Incremental indexing failed:', error);
          scheduleNext();
        }
      }, interval);
    };

    scheduleNext();
    console.log(`Periodic indexing scheduled every ${interval / 1000} seconds`);
  }

  /**
   * Perform incremental indexing of new/changed documents
   */
  private async performIncrementalIndexing(): Promise<void> {
    if (!this.isRunning) return;

    console.log('Performing incremental indexing...');

    try {
      const documents = await this.processor.processAutoSources();
      const newDocuments = documents.filter(doc => 
        !doc.metadata.filePath || !this.processedFiles.has(doc.metadata.filePath)
      );

      console.log(`Found ${newDocuments.length} new documents`);

      if (newDocuments.length === 0) {
        return;
      }

      // Process new documents
      await this.processBatch(newDocuments);

      // Update tracking
      newDocuments.forEach(doc => {
        if (doc.metadata.filePath) {
          this.processedFiles.add(doc.metadata.filePath);
        }
      });

      this.lastIndexTime = Date.now();

      // Send analytics event
      await this.recordAnalyticsEvent({
        type: 'ingestion',
        timestamp: Date.now(),
        pipelineName: 'continuous-indexer',
        requestId: generateId(),
        metadata: {
          documentsCount: newDocuments.length,
          processingTime: Date.now() - this.lastIndexTime,
        },
      });

      console.log(`Incremental indexing completed: ${newDocuments.length} documents processed`);
    } catch (error) {
      console.error('Incremental indexing failed:', error);
      
      // Record error event
      await this.recordAnalyticsEvent({
        type: 'error',
        timestamp: Date.now(),
        pipelineName: 'continuous-indexer',
        requestId: generateId(),
        metadata: {
          error: String(error),
          errorCode: 'INCREMENTAL_INDEXING_FAILED',
        },
      });
    }
  }

  /**
   * Process a batch of documents across all registered pipelines
   */
  private async processBatch(documents: Document[]): Promise<void> {
    const promises: Promise<void>[] = [];

    for (const [pipelineName, pipeline] of this.pipelines) {
      const pipelinePromise = this.processBatchForPipeline(documents, pipeline, pipelineName);
      promises.push(pipelinePromise);
    }

    await Promise.allSettled(promises);
  }

  /**
   * Process documents for a specific pipeline
   */
  private async processBatchForPipeline(
    documents: Document[],
    pipeline: RAGPipeline,
    pipelineName: string
  ): Promise<void> {
    try {
      const ingestionRequests = documents.map(doc => ({
        source: doc.content,
        type: 'text' as const,
        format: doc.metadata.format,
        metadata: doc.metadata,
        recursive: false,
      }));

      await pipeline.ingest(ingestionRequests);
      console.log(`Pipeline ${pipelineName}: processed ${documents.length} documents`);
    } catch (error) {
      console.error(`Pipeline ${pipelineName} processing failed:`, error);
      
      // Record pipeline-specific error
      await this.recordAnalyticsEvent({
        type: 'error',
        timestamp: Date.now(),
        pipelineName,
        requestId: generateId(),
        metadata: {
          error: String(error),
          errorCode: 'PIPELINE_PROCESSING_FAILED',
          documentsCount: documents.length,
        },
      });
    }
  }

  /**
   * Handle webhook events for real-time indexing
   */
  async handleWebhookEvent(event: any): Promise<void> {
    if (!this.isRunning) {
      console.warn('Received webhook event but indexer is not running');
      return;
    }

    try {
      console.log('Processing webhook event:', event.type);

      // Process documents from webhook
      const documents = await this.processor.processWebhookPayload(event.payload, event.signature);

      if (documents.length > 0) {
        await this.processBatch(documents);
        
        // Update tracking
        documents.forEach(doc => {
          if (doc.metadata.filePath) {
            this.processedFiles.add(doc.metadata.filePath);
          }
        });

        console.log(`Webhook event processed: ${documents.length} documents`);
      }
    } catch (error) {
      console.error('Webhook event processing failed:', error);
      
      await this.recordAnalyticsEvent({
        type: 'error',
        timestamp: Date.now(),
        pipelineName: 'webhook-handler',
        requestId: generateId(),
        metadata: {
          error: String(error),
          errorCode: 'WEBHOOK_PROCESSING_FAILED',
        },
      });
    }
  }

  /**
   * Force reindexing of all documents
   */
  async forceReindexing(): Promise<void> {
    console.log('Starting force reindexing...');
    
    // Clear processed files tracking
    this.processedFiles.clear();
    this.lastIndexTime = 0;

    // Perform full indexing
    await this.performInitialIndexing();

    console.log('Force reindexing completed');
  }

  /**
   * Get indexing status and statistics
   */
  getStatus(): {
    isRunning: boolean;
    lastIndexTime: number;
    processedFilesCount: number;
    registeredPipelines: number;
  } {
    return {
      isRunning: this.isRunning,
      lastIndexTime: this.lastIndexTime,
      processedFilesCount: this.processedFiles.size,
      registeredPipelines: this.pipelines.size,
    };
  }

  /**
   * Load previous state from storage
   */
  private async loadState(): Promise<void> {
    try {
      const state = await this.env.RAG_KV.get('continuous-indexer:state');
      if (state) {
        const parsed = JSON.parse(state);
        this.processedFiles = new Set(parsed.processedFiles || []);
        this.lastIndexTime = parsed.lastIndexTime || 0;
        console.log(`Loaded state: ${this.processedFiles.size} processed files`);
      }
    } catch (error) {
      console.warn('Failed to load previous state:', error);
    }
  }

  /**
   * Save current state to storage
   */
  private async saveState(): Promise<void> {
    try {
      const state = {
        processedFiles: Array.from(this.processedFiles),
        lastIndexTime: this.lastIndexTime,
        timestamp: Date.now(),
      };

      await this.env.RAG_KV.put('continuous-indexer:state', JSON.stringify(state));
      console.log('State saved');
    } catch (error) {
      console.warn('Failed to save state:', error);
    }
  }

  /**
   * Record analytics event
   */
  private async recordAnalyticsEvent(event: RAGAnalyticsEvent): Promise<void> {
    try {
      if (this.env.ANALYTICS) {
        await this.env.ANALYTICS.writeDataPoint({
          blobs: [
            JSON.stringify(event),
          ],
          doubles: [
            event.timestamp,
          ],
          indexes: [
            event.type,
            event.pipelineName,
          ],
        });
      }
    } catch (error) {
      console.warn('Failed to record analytics event:', error);
    }
  }

  /**
   * Create batches from array
   */
  private createBatches<T>(items: T[], batchSize: number): T[][] {
    const batches: T[][] = [];
    for (let i = 0; i < items.length; i += batchSize) {
      batches.push(items.slice(i, i + batchSize));
    }
    return batches;
  }

  /**
   * Utility delay function
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Clean up resources
   */
  async cleanup(): Promise<void> {
    await this.stop();
    this.pipelines.clear();
    this.processedFiles.clear();
  }
}

/**
 * Factory function to create continuous indexer
 */
export function createContinuousIndexer(env: RAGEnv, config: AutoIndexingConfig): ContinuousIndexer {
  return new ContinuousIndexer(env, config);
}

/**
 * Cloudflare Workers Cron handler for scheduled indexing
 */
export async function handleScheduledIndexing(env: RAGEnv, cron: string): Promise<void> {
  console.log(`Scheduled indexing triggered: ${cron}`);
  
  try {
    // Load indexer configuration
    const configData = await env.RAG_KV.get('continuous-indexer:config');
    if (!configData) {
      console.warn('No indexer configuration found');
      return;
    }

    const config = JSON.parse(configData);
    const indexer = createContinuousIndexer(env, config);

    // Perform incremental indexing
    await indexer.start();
    
    // Run for a short period then stop
    setTimeout(async () => {
      await indexer.stop();
    }, 4 * 60 * 1000); // Run for 4 minutes (within 5-minute cron limit)

  } catch (error) {
    console.error('Scheduled indexing failed:', error);
  }
}